import apiClient from './api';
import type {
  GetAllReviewsResponse,
  ApproveReviewResponse,
  DeclineReviewResponse,
  APIPaginatedResponse,
  PendingReview,
  CatalogueItemActionResponse,
  CatalogueServiceBulkApproveRequest,
  CatalogueBulkApproveResponse,
  BulkApproveReviewsResponse,
  MessageInfo,
  CatalogueItemVersionID,
} from '../types';
import { buildErrorResponse } from '../utils/api';

export const reviewsService = {
  /**
   * Get all pending reviews
   * Note: Backend supports cursor pagination with afterCursor and limit params
   */
  getAllReviews: async (
    status?: 'pending' | 'accepted' | 'declined',
    limit: number = 8,
    afterCursor?: string
  ): Promise<APIPaginatedResponse<PendingReview>> => {
    try {
      const response = await apiClient.get<GetAllReviewsResponse>('/admins/reviews', {
        params: {
          status: status || 'pending',
          afterCursor,
          limit
        }
      });

      return {
        ok: true,
        data: response.data.data,
      };
    } catch (error) {
      return buildErrorResponse('Error fetching reviews', error);
    }
  },

  /**
   * Approve a review
   * POST /admins/reviews/{reviewId}/approve
   */
  approveReview: async (
    reviewId: string,
    versionId: CatalogueItemVersionID,
  ): Promise<CatalogueItemActionResponse<PendingReview>> => {
    try {
      const response = await apiClient.post<ApproveReviewResponse>(
        `/admins/reviews/${reviewId}/approve`,
        {
          versionId,
        },
      );

      return {
        ok: true,
        data: response.data.data,
        messageInfo: {
          message: 'Review approved successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error approving review', error);
    }
  },

  /**
   * Decline a review
   * POST /admins/reviews/{reviewId}/decline
   */
  declineReview: async (
    reviewId: string,
    versionId: CatalogueItemVersionID,
  ): Promise<CatalogueItemActionResponse<PendingReview>> => {
    try {
      const response = await apiClient.post<DeclineReviewResponse>(
        `/admins/reviews/${reviewId}/decline`,
        {
          versionId,
        },
      );

      return {
        ok: true,
        data: response.data.data,
        messageInfo: {
          message: 'Review declined successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error declining review', error);
    }
  },

  /**
   * Bulk approve reviews
   * POST /admins/reviews/bulk-approve
   * Body: { "reviews": [{ "reviewId": "...", "versionId": 1 }, ...] }
   */
  bulkApproveReviews: async (reviews: CatalogueServiceBulkApproveRequest<'reviewId', string>): Promise<CatalogueBulkApproveResponse<'reviewId', string>> => {
    try {
      const response = await apiClient.post<BulkApproveReviewsResponse>('/admins/reviews/bulk-approve', {
        reviews,
      });
      const data = response.data.data;

      const results = data.results
      const totalCount = results.length;
      const successCount = results.filter(item => item.success).length;
      const failedCount = totalCount - successCount;

      let messageInfo: MessageInfo;

      if (failedCount === 0) {
        messageInfo = {
          message: `Successfully approved all ${successCount} review(s)!`,
        }
      } else if (successCount === 0) {
        messageInfo = {
          error: `Failed to approve all ${failedCount} review(s)`,
        }
      } else {
        messageInfo = {
          warning: `Approved ${successCount} of ${totalCount} review(s). ${failedCount} failed.`,
        }
      }

      return {
        ok: true,
        data: {
          results,
        },
        messageInfo
      }
    } catch (error) {
      return buildErrorResponse('Error approving reviews', error);
    }
  },
};
